import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "tools/routerCompat";
import { cloneDeep, omit } from "lodash";
import { changeAppraiserAssigned } from "actions/homeowner/casedetails";
import { getAppraiserListByAppraisalId } from "actions/appraiser";
import {
  DATA_TYPE_EMAIL,
  DATA_TYPE_APPRAISER,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_HYPERLINK,
  DATA_TYPE_JSX,
} from "typeConstants/dataTypes";
import { caseTypes, caseTypeDefault, userDefault, userShape } from "components/Utility";
import { Capitalize, notProvided } from "components/Shared/Formatters";
import { assignCasesToInspector, unassignCases } from "actions/assignment";
import { FIELDREP_TOOLTIPS_ME, EMPLOYEE_CONTACT_VIEW, CASE_HEADER_AGENT_CODE_EDIT } from "typeConstants/rights";
import { MSpinner, MCard, MDialog, ACTION_CLOSE } from "components/Shared";
import AssignedAppraiserAuditPopup from "components/QualityAssurance/Sections/CaseDetails/AssignedAppraiserAuditPopup";
import { updateCaseHeader } from "requests/homeowner/caseforms";
import { CaseFieldLabels } from "tools/caseTypeLabels";
import AgentSearch from "components/QualityAssurance/Agent/AgentSearch";
import { getFRSortFunc } from "components/Shared/Functions";
import responseHandler from "responseHandler";
import { MAJOR_COMPANY_VIEW_FR_TOOLTIP } from "typeConstants/customerFeatures";

class ContactInformation extends React.Component {
  constructor(props) {
    super(props);
    this.getFields = this.getFields.bind(this);
    this.getInsuranceFields = this.getInsuranceFields.bind(this);
    this.getValuationFields = this.getValuationFields.bind(this);
    this.getPolicyHolderFields = this.getPolicyHolderFields.bind(this);
    this.getBorrowerFields = this.getBorrowerFields.bind(this);
    this.getAppraiserFields = this.getAppraiserFields.bind(this);
    this.getBrokerFields = this.getBrokerFields.bind(this);
    this.constructUser = this.constructUser.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleAppraiserEdit = this.handleAppraiserEdit.bind(this);
    this.getAgencyCodeFields = this.getAgencyCodeFields.bind(this);
    this.getUnderwriterCodeFields = this.getUnderwriterCodeFields.bind(this);
    this.getAssignedAppraiserDialog = this.getAssignedAppraiserDialog.bind(this);
    this.submitAssignedAppraiserReason = this.submitAssignedAppraiserReason.bind(this);
    this.getValuationCoordinatorFields = this.getValuationCoordinatorFields.bind(this);
    this.getValuationDirectorFields = this.getValuationDirectorFields.bind(this);

    this.mounted = { current: true };

    this.state = {
      newAppraiserAssigned: {},
      showAssignedAppraiserReasonDialog: false,
      isShowingAgentDialog: false,
      appraiserAssignmentReasonNeeded: false,
      appraiserProximityCommentNeeded: false,
      appraiserLicenseCommentNeeded: false,
    };

    this.changedFields = {};
  }

  componentDidMount() {
    const { canChangeAppraiserAssignment, getAppraiserListByAppraisalId, controlNumber, caseType } = this.props;
    // Check for controlNumber 0 because of the way the parent component is handling it
    if (caseType === caseTypes.valuation && canChangeAppraiserAssignment && controlNumber !== 0) {
      getAppraiserListByAppraisalId(controlNumber);
    }
  }

  componentWillUnmount() {
    this.mounted.current = false;
  }

  getFields() {
    const { caseType } = this.props;
    switch (caseType) {
      case caseTypes.insurance:
        return this.getInsuranceFields();
      case caseTypes.valuation:
        return this.getValuationFields();
    }
  }

  getInsuranceFields() {
    return [
      ...this.getPolicyHolderFields(),
      getFieldRepFields(this.props),
      this.getAgentFields(),
      this.getAgencyCodeFields(),
      this.getFieldSurveyDirectorFields(),
      this.getFieldSurveyCoordinatorFields(),
      this.getQaDirectorFields(),
      this.getUnderwriterCodeFields(),
    ].filter((field) => field);
  }

  getValuationFields() {
    return [
      ...this.getBorrowerFields(),
      getFieldRepFields(this.props),
      this.getAppraiserFields(),
      this.getValuationCoordinatorFields(),
      this.getValuationDirectorFields(),
      this.getBrokerFields(),
      this.getLenderDirectorFields(),
      this.getLenderManagerFields(),
      this.getFieldSurveyDirectorFields(),
      this.getFieldSurveyCoordinatorFields(),
      this.getQaDirectorFields(),
      this.getAgentFields(),
      this.getAgencyCodeFields(),
    ].filter((field) => field);
  }

  getPolicyHolderFields() {
    const { policyholder, caseType } = this.props;
    return [
      {
        key: "ph-header",
        label: "Policyholder",
        value: policyholder.name,
        editable: true,
        editFields: [
          {
            key: "policyholderName1",
            label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " First Name",
            value: policyholder.firstName,
            editable: true,
          },
          {
            key: "policyholderName2",
            label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " Last Name",
            value: policyholder.lastName,
            editable: true,
          },
        ],
      },
      {
        key: "policyholderPhoneHome",
        dataType: DATA_TYPE_PHONE_NUMBER,
        label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " Home #",
        value: policyholder.homePhone,
        editable: true,
      },
      {
        key: "policyholderPhoneWork",
        dataType: DATA_TYPE_PHONE_NUMBER,
        label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " Work #",
        value: policyholder.workPhone,
        editable: true,
      },
      {
        key: "policyholderEmail",
        dataType: DATA_TYPE_EMAIL,
        label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " Email",
        value: policyholder.email,
        editable: true,
      },
      {
        key: "designatedContactName",
        label: "Designated Contact Name",
        value: policyholder.designatedContactName,
        editable: true,
      },
      {
        key: "designatedContactPhone1",
        dataType: DATA_TYPE_PHONE_NUMBER,
        label: "Designated Contact Phone 1",
        value: policyholder.designatedContactPhone1,
        editable: true,
      },
      {
        key: "designatedContactPhone2",
        dataType: DATA_TYPE_PHONE_NUMBER,
        label: "Designated Contact Phone 2",
        value: policyholder.designatedContactPhone2,
        editable: true,
      },
      {
        key: "designatedContactEmail",
        dataType: DATA_TYPE_EMAIL,
        label: "Designated Contact Email",
        value: policyholder.designatedContactEmail,
        editable: true,
      },
    ];
  }

  getLenderDirectorFields() {
    const { rights, lenderDirector } = this.props;
    if (rights.includes(EMPLOYEE_CONTACT_VIEW)) {
      const fields = constructUserContactInformation(lenderDirector, "lld");
      if (fields.length > 0)
        return {
          key: "lld-header",
          label: "Lender Director",
          value: lenderDirector.name,
          tooltip: { title: "Lender Director Information", fields },
          editable: false,
        };
    }
    return null;
  }

  getLenderManagerFields() {
    const { rights, lenderManager } = this.props;
    if (rights.includes(EMPLOYEE_CONTACT_VIEW)) {
      const fields = constructUserContactInformation(lenderManager, "llm");
      if (fields.length > 0)
        return {
          key: "llm-header",
          label: "Lender Manager",
          value: lenderManager.name,
          tooltip: { title: "Lender Manager Information", fields },
          editable: false,
        };
    }
    return null;
  }

  getFieldSurveyDirectorFields() {
    const { rights, fieldSurveyDirector } = this.props;
    if (rights.includes(EMPLOYEE_CONTACT_VIEW)) {
      const fields = constructUserContactInformation(fieldSurveyDirector, "fsd");
      if (fields.length > 0)
        return {
          key: "fsd-header",
          label: "Field Director",
          value: fieldSurveyDirector.name,
          tooltip: { title: "Field Director Information", fields },
          editable: false,
        };
    }
    return null;
  }

  getFieldSurveyCoordinatorFields() {
    const { rights, fieldSurveyCoordinator } = this.props;
    if (rights.includes(EMPLOYEE_CONTACT_VIEW)) {
      const fields = constructUserContactInformation(fieldSurveyCoordinator, "fsc");
      if (fields.length > 0)
        return {
          key: "fsc-header",
          label: "Field Manager",
          value: fieldSurveyCoordinator.name,
          tooltip: { title: "Field Manager Information", fields },
          editable: false,
        };
    }
    return null;
  }

  getQaDirectorFields() {
    const { rights, qaDirector } = this.props;
    if (rights.includes(EMPLOYEE_CONTACT_VIEW)) {
      const fields = constructUserContactInformation(qaDirector, "bom");
      if (fields.length > 0)
        return {
          key: "bom-header",
          label: "QA Director",
          value: qaDirector.name,
          tooltip: { title: "QA Director Information", fields },
          editable: false,
        };
    }
    return null;
  }

  getBorrowerFields() {
    const { borrower, caseType } = this.props;
    return [
      {
        key: "bo-header",
        label: CaseFieldLabels.PolicyHolder.getLabel(caseType),
        value: borrower.name,
        editable: true,
        editFields: [
          {
            key: "policyholderName1",
            label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " First Name",
            value: borrower.firstName,
            editable: true,
          },
          {
            key: "policyholderName2",
            label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " Last Name",
            value: borrower.lastName,
            editable: true,
          },
        ],
      },
      {
        key: "policyholderPhoneHome",
        dataType: DATA_TYPE_PHONE_NUMBER,
        label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " Home #",
        value: borrower.homePhone,
        editable: true,
      },
      {
        key: "policyholderPhoneWork",
        dataType: DATA_TYPE_PHONE_NUMBER,
        label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " Work #",
        value: borrower.workPhone,
        editable: true,
      },
      {
        key: "policyholderEmail",
        dataType: DATA_TYPE_EMAIL,
        label: CaseFieldLabels.PolicyHolder.getLabel(caseType) + " Email",
        value: borrower.email,
        editable: true,
      },
      {
        key: "designatedContactName",
        label: "Designated Contact Name",
        value: borrower.designatedContactName,
        editable: true,
      },
      {
        key: "designatedContactPhone1",
        dataType: DATA_TYPE_PHONE_NUMBER,
        label: "Designated Contact Phone 1",
        value: borrower.designatedContactPhone1,
        editable: true,
      },
      {
        key: "designatedContactPhone2",
        dataType: DATA_TYPE_PHONE_NUMBER,
        label: "Designated Contact Phone 2",
        value: borrower.designatedContactPhone2,
        editable: true,
      },
      {
        key: "designatedContactEmail",
        dataType: DATA_TYPE_EMAIL,
        label: "Designated Contact Email",
        value: borrower.designatedContactEmail,
        editable: true,
      },
    ];
  }

  getAppraiserFields() {
    const { appraiser, assignableAppraisers, canChangeAppraiserAssignment, isEditable } = this.props;
    let modifiedAppraiserObj = omit(cloneDeep(appraiser), ["appraiserId", "allowsEdits", "internetUserId"]);
    if (modifiedAppraiserObj.name) {
      // For name it should be a { label, onClick } for type DATA_TYPE_HYPERLINK
      modifiedAppraiserObj.name = { label: appraiser.name, onClick: () => {} }; //clicking this used to split out the appraiser info with the old splitter, it hasnt worked in forever so im not going to take the time to make it work
    }
    const fields = constructUserContactInformation(modifiedAppraiserObj, "ap");
    let accountFeatures =
      assignableAppraisers?.assignmentReasonFeature || assignableAppraisers?.proximityCommentFeature;
    let result = {
      key: "ap-header",
      label: "Appraiser",
      value: appraiser.name ? `${appraiser.name}/${appraiser.allowsEdits}/${accountFeatures}` : null,
      dataType: DATA_TYPE_APPRAISER,
      ...(fields.length > 0 && { tooltip: { title: "Appraiser Information", fields } }),
      editable: false,
    };

    if (isEditable) {
      return {
        ...result,
        editFields: [
          {
            key: "ap-header",
            label: "Appraiser",
            dataType: DATA_TYPE_DROPDOWN2,
            value: {
              label: appraiser.name,
              value: { appraiserNumber: appraiser.number, appraiserId: appraiser.appraiserId },
            },
            dataTypeOptions: {
              options: assignableAppraisers?.appraisersAvailable?.map((x) => {
                return {
                  label:
                    (x.isSalary ? "S-" : "") +
                    x.fullName +
                    ` (${x.performanceGrade}; ${x.proximityToSubject} mi` +
                    (x.licenseExpiringSoonDate ? `; Lic Exp: ${x.licenseExpiringSoonDate}` : "") +
                    ")",
                  value: { appraiserNumber: x.appraiserNumber, appraiserId: x.id },
                };
              }),
            },
            onChange: (option) => {
              // Account features that require a reason why appraiser with lower score was selected or when more than 150 miles from subject
              assignableAppraisers?.assignmentReasonFeature || assignableAppraisers?.proximityCommentFeature
                ? this.setState({ newAppraiserAssigned: option })
                : undefined;
            },
            editable: canChangeAppraiserAssignment,
          },
        ],
      };
    }

    return result;
  }

  getValuationCoordinatorFields() {
    const { valuationCoordinator } = this.props;
    if (valuationCoordinator) {
      const fields = constructUserContactInformation(valuationCoordinator, "valCoord");
      return {
        key: "valCoord-header",
        label: "Val. Coordinator",
        value: valuationCoordinator.name,
        tooltip: { title: "Valuation Coordinator Information", fields },
        editable: false,
      };
    }
  }

  getValuationDirectorFields() {
    const { valuationDirector } = this.props;
    if (valuationDirector) {
      const fields = constructUserContactInformation(valuationDirector, "valDir");
      return {
        key: "valDir-header",
        label: "Val. Director",
        value: valuationDirector.name,
        tooltip: { title: "Valuation Director Information", fields },
        editable: false,
      };
    }
  }

  getAssignedAppraiserDialog() {
    const { assignableAppraisers } = this.props;
    let newlyAssignedAppraiserId = this.state.newAppraiserAssigned?.value?.appraiserId;
    let selectedAppraiser = assignableAppraisers?.appraisersAvailable?.find((aa) => aa.id === newlyAssignedAppraiserId);
    let needProximityComment =
      assignableAppraisers?.proximityCommentFeature && Number(selectedAppraiser?.proximityToSubject) > 150; // Miles
    let needAssignmentComment = false;
    if (selectedAppraiser && assignableAppraisers?.assignmentReasonFeature) {
      let cmp = (x, y) => (x > y) - (x < y);
      let sortedGrades = assignableAppraisers?.appraisersAvailable
        ?.map((aa) => aa.performanceGrade)
        .sort((x, y) => cmp(x, y));
      if (sortedGrades.length > 1) {
        const highestGrade = sortedGrades.shift();
        needAssignmentComment =
          (sortedGrades.some((g) => g === highestGrade) && highestGrade === selectedAppraiser.performanceGrade) ||
          highestGrade !== selectedAppraiser.performanceGrade;
      }
    }
    // This value will be null unless the license is expiring in the next 10 days and then a comment is required
    let needsLicenseComment = selectedAppraiser && selectedAppraiser?.licenseExpiringSoonDate !== null;
    if (needProximityComment || needAssignmentComment || needsLicenseComment) {
      this.setState({
        showAssignedAppraiserReasonDialog: true,
        appraiserProximityCommentNeeded: needProximityComment,
        appraiserAssignmentReasonNeeded: needAssignmentComment,
        appraiserLicenseCommentNeeded: needsLicenseComment,
      });
    } else {
      selectedAppraiser
        ? this.submitAssignedAppraiserReason() // Uses default reason
        : this.submitAssignedAppraiserReason(null); // No reason
    }
  }

  getBrokerFields() {
    const { broker } = this.props;
    const fields = constructUserContactInformation(broker, "br");
    if (fields.length > 0)
      return {
        key: "br-header",
        label: "Broker",
        value: broker.name,
        tooltip: { title: "Broker Information", fields },
        editable: false,
      };
    return null;
  }

  getAgentFields() {
    const { agent, caseAgent } = this.props;
    const fields = constructUserContactInformation(agent, "ag");
    const showEdit = this.props.rights.some((r) => r === CASE_HEADER_AGENT_CODE_EDIT);

    // Use case agent phone number if available
    if (caseAgent.workPhone) {
      let agent_idx = fields.findIndex((x) => x.key == "ag-workPhone");
      if (agent_idx >= 0) {
        fields[agent_idx].value = caseAgent.workPhone;
      }
    }

    return {
      key: "ag-header",
      label: "Agent",
      value: caseAgent?.name ? (
        <>
          {caseAgent.name}
          {showEdit && (
            <i
              onClick={() => this.setState({ isShowingAgentDialog: true })}
              className="md-icon material-icons edit-agent-icon"
              title="Edit Agent"
            >
              edit
            </i>
          )}
        </>
      ) : null,
      tooltip: { title: "Agent Information", fields: fields.length > 0 ? fields : undefined },
      editFields: false,
      dataType: DATA_TYPE_JSX,
    };
  }

  getAgencyCodeFields() {
    const { agent, caseAgent, reportType } = this.props;
    if (reportType === "1004P APPRAISAL") return null;
    return {
      key: "agc-header",
      label: "Agency Code",
      value: caseAgent && caseAgent.code ? caseAgent.code : agent && agent.code ? agent.code : notProvided,
      tooltip: null,
      editFields: null,
    };
  }

  getUnderwriterCodeFields() {
    const { underwriterCode } = this.props;
    return {
      key: "agmc-header",
      label: "Underwriter Code",
      value: underwriterCode ? underwriterCode : notProvided,
      tooltip: null,
      editFields: null,
    };
  }

  constructUser(userProp) {
    return { ...userDefault, ...userProp };
  }

  handleAppraiserEdit(obj) {
    const { changeAppraiserAssigned, controlNumber, getCaseHeader, assignCasesToInspector, unassignCases, appraiser } =
      this.props;
    const localProps = this.props;
    if (obj?.hasOwnProperty("ap-header")) {
      let newAppraiserObj = obj["ap-header"];
      delete obj["ap-header"];
      // If edit has appraiser but originally the case did not have appraiser
      let hadAppraiserAssigned = appraiser.name;
      const proximityComment = obj["ap-header-proximityComment"] ?? null;
      const caseNoteBody = obj["ap-header-appraiserAssignment-caseNoteBody"] ?? null;
      let caseAssignmentDetails = {
        caseId: controlNumber,
        approvalRequestComments: proximityComment,
        caseNoteBody: caseNoteBody,
      };
      const handleAssignmentReturn = (assignmentResults) => {
        if (assignmentResults?.caseAssignmentDetails?.length) {
          // show toast message to the user if assignment details were returned
          const toastMessage = assignmentResults.caseAssignmentDetails
            .map((e) => `${e.caseId} ${e.message}`)
            .join("\n")
            .trim();
          if (toastMessage) {
            localProps.makeToast(toastMessage, "warning");
          }
        }
        getCaseHeader(controlNumber);
      };
      if (newAppraiserObj && !hadAppraiserAssigned) {
        assignCasesToInspector("appraisal", [controlNumber], newAppraiserObj.appraiserNumber, handleAssignmentReturn, [
          caseAssignmentDetails,
        ]);
      } else if (!newAppraiserObj && hadAppraiserAssigned) {
        unassignCases("appraisal", [controlNumber], () => getCaseHeader(controlNumber));
      } else if (hadAppraiserAssigned && newAppraiserObj) {
        changeAppraiserAssigned(
          controlNumber,
          newAppraiserObj.appraiserId,
          handleAssignmentReturn,
          caseAssignmentDetails,
        );
      }
    }
  }

  handleInspectorEdit(obj) {
    const { controlNumber, getCaseHeader, assignCasesToInspector, unassignCases, fieldRep } = this.props;
    if (obj?.hasOwnProperty("inspectorNumber")) {
      let newInspector = obj["inspectorNumber"];
      delete obj["inspectorNumber"];
      let hadInspectorAssigned = fieldRep.number;
      if (newInspector && !hadInspectorAssigned) {
        assignCasesToInspector("insurance", [controlNumber], newInspector, () => getCaseHeader(controlNumber));
      } else if (!newInspector && hadInspectorAssigned) {
        unassignCases("insurance", [controlNumber], () => getCaseHeader(controlNumber));
      } else if (newInspector !== hadInspectorAssigned) {
        unassignCases("insurance", [controlNumber], () =>
          assignCasesToInspector("insurance", [controlNumber], newInspector, () => getCaseHeader(controlNumber)),
        );
      }
    }
  }

  handleSave(arr, obj) {
    const { controlNumber, getCaseHeader, fieldRep, updateCaseHeader } = this.props;
    let frChanged = obj.hasOwnProperty("inspectorNumber");
    let appraiserChanged = obj.hasOwnProperty("ap-header");
    let otherFieldsChanged = Object.keys(omit({ ...obj }, ["ap-header", "inspectorNumber"])).length > 0;
    if (otherFieldsChanged && !frChanged && !appraiserChanged) {
      updateCaseHeader(controlNumber, obj, () => getCaseHeader(controlNumber), this.mounted);
    } else if (otherFieldsChanged && frChanged && !appraiserChanged) {
      updateCaseHeader(controlNumber, obj, () => this.handleInspectorEdit(obj), this.mounted);
    } else if (frChanged && !appraiserChanged) {
      this.handleInspectorEdit(obj, fieldRep.number);
    } else if (appraiserChanged) {
      this.changedFields = obj;
      this.getAssignedAppraiserDialog();
    }
  }

  submitAssignedAppraiserReason(
    option = "Highest rated appraiser selected",
    assignComment = null,
    proximityComment = null,
    licenseComment = null,
  ) {
    let payload = {
      noteBody: null,
      isExternal: false,
    };
    let addToNoteBody = (note) =>
      payload.noteBody?.length ? (payload.noteBody += `; ${note}`) : (payload.noteBody = note);
    if (option) {
      if (option === "Highest rated appraiser selected") {
        payload.noteBody = option;
      } else {
        payload.noteBody = `Assignment Reason: ${option !== "Other" ? option : assignComment}`;
        this.props.makeToast("Appraiser assignment details submitted.", "success");
      }
    }
    if (proximityComment) {
      addToNoteBody(`Assignment over 150 mi: ${proximityComment}`);
      this.changedFields["ap-header-proximityComment"] = proximityComment;
    }
    if (licenseComment) {
      addToNoteBody(`“Assignment w/ license expiring in 10 days: ${licenseComment}`);
    }
    if (payload.noteBody) {
      this.changedFields["ap-header-appraiserAssignment-caseNoteBody"] = payload.noteBody;
    }
    if (option || proximityComment) {
      this.setState({
        showAssignedAppraiserReasonDialog: false,
        newAppraiserAssigned: {},
        appraiserProximityCommentNeeded: false,
        appraiserAssignmentReasonNeeded: false,
        appraiserLicenseCommentNeeded: false,
      });
    }
    const { updateCaseHeader, controlNumber } = this.props;
    let frChanged = this.changedFields.hasOwnProperty("inspectorNumber");
    let otherFieldsChanged = Object.keys(omit({ ...this.changedFields }, ["ap-header", "inspectorNumber"])).length > 0;
    if (otherFieldsChanged) {
      updateCaseHeader(controlNumber, this.changedFields, () => {
        this.handleAppraiserEdit(this.changedFields);
        if (frChanged) this.handleInspectorEdit(this.changedFields);
      });
    } else {
      this.handleAppraiserEdit(this.changedFields);
      if (frChanged) this.handleInspectorEdit(this.changedFields);
    }
  }

  render() {
    const { className, policyholder } = this.props;
    if (!policyholder) {
      return <MSpinner />;
    }
    return (
      <>
        {this.state.showAssignedAppraiserReasonDialog && (
          <AssignedAppraiserAuditPopup
            submitAssignedAppraiserReason={this.submitAssignedAppraiserReason}
            proximityCommentNeeded={this.state.appraiserProximityCommentNeeded}
            assignedReasonNeeded={this.state.appraiserAssignmentReasonNeeded}
            licenseCommentNeeded={this.state.appraiserLicenseCommentNeeded}
          />
        )}
        {this.state.isShowingAgentDialog && (
          <MDialog
            id="agent-edit-dialog"
            title="Agent Details"
            actions={[{ code: ACTION_CLOSE, onClick: () => this.setState({ isShowingAgentDialog: false }) }]}
            width="75%"
          >
            <AgentSearch
              controlIDNumber={this.props.controlNumber}
              onAgentSelected={(agent) => {
                this.setState({ isShowingAgentDialog: false });
                updateCaseHeader(
                  this.props.controlNumber,
                  {
                    "PolicyInfo.CaseAgentInfo.Code": agent.agentCode,
                    "PolicyInfo.CaseAgentInfo.AgentName": agent.agentName,
                    "PolicyInfo.CaseAgentInfo.AgentPhone": agent.phoneNumber,
                    "PolicyInfo.CaseAgentInfo.AgentId": agent.id,
                  },
                  () => this.props.updateCaseHeader(this.props.controlNumber, {}),
                  this.mounted,
                );
              }}
            />
          </MDialog>
        )}
        <MCard
          title="Contact Information"
          className={className}
          fields={this.getFields()}
          isEditable={this.props.isEditable}
          onEditFinished={this.handleSave}
        />
      </>
    );
  }
}

ContactInformation.defaultProps = {
  policyholder: userDefault,
  fieldRep: userDefault,
  appraiser: userDefault,
  broker: userDefault,
  caseType: caseTypeDefault,
  className: null,
  agent: userDefault,
  caseAgent: userDefault,
};

ContactInformation.propTypes = {
  ///Field Values
  policyholder: PropTypes.object,
  fieldRep: userShape,
  borrower: PropTypes.object,
  appraiser: userShape,
  broker: userShape,
  agent: userShape,
  caseAgent: userShape,
  underwriterCode: PropTypes.string,
  ///Style
  className: PropTypes.string,
  ///Appraisal or Insurance caseType
  caseType: PropTypes.string,
  updateCaseHeader: PropTypes.func,
  getCaseHeader: PropTypes.func,
  isEditable: PropTypes.bool,
  FRList: PropTypes.array,
  controlNumber: PropTypes.number,
  caseStatus: PropTypes.string,

  assignableAppraisers: PropTypes.object,
  canChangeAppraiserAssignment: PropTypes.bool,
  getAppraiserListByAppraisalId: PropTypes.func.isRequired,
  changeAppraiserAssigned: PropTypes.func.isRequired,
  isFetchingAssignableAppraisers: PropTypes.bool.isRequired,
  isChangingAppraiserAssigned: PropTypes.bool.isRequired,
  assignCasesToInspector: PropTypes.func.isRequired,
  unassignCases: PropTypes.func.isRequired,
  unassignStatus: PropTypes.object.isRequired,
  assignStatus: PropTypes.object.isRequired,
  lenderDirector: PropTypes.object,
  lenderManager: PropTypes.object,
  fieldSurveyCoordinator: PropTypes.object,
  fieldSurveyDirector: PropTypes.object,
  valuationCoordinator: PropTypes.object,
  valuationDirector: PropTypes.object,
  qaDirector: PropTypes.object,
  rights: PropTypes.array,
  reportType: PropTypes.string,
  makeToast: PropTypes.func,
  enabledFeatures: PropTypes.array,
  completedDate: PropTypes.string,
};

const constructUserContactInformation = (userObject, keyPrefix) => {
  let userContactInformation = [];
  if (!userObject) return userContactInformation;

  if (userObject?.nameNumberDto?.firstLast) {
    userContactInformation.push({
      key: `${keyPrefix}-firstLast`,
      label: Capitalize(getLabel("name")),
      value: userObject.nameNumberDto.firstLast,
      dataType: getDataType("firstLast", keyPrefix),
    });
  }

  for (const propertyName in userObject) {
    if (propertyName === "nameNumberDto") continue;

    let property = userObject[propertyName];
    if (!property) continue;

    userContactInformation.push({
      key: `${keyPrefix}-${propertyName}`,
      label: Capitalize(getLabel(propertyName)),
      value: property,
      dataType: getDataType(propertyName, keyPrefix),
    });
  }
  return userContactInformation;
};

const getLabel = (propertyName) => {
  switch (propertyName) {
    case "name":
      return "name";
    case "number":
      return "#";
    case "workPhone":
      return "work #";
    case "mobilePhone":
      return "mobile #";
    case "homePhone":
      return "home #";
    case "email":
      return "email";
    case "personalEmail":
      return "Personal Email";
    case "phoneNumber":
      return "Phone #";
    case "code":
      return "Code";
    default:
      return "property not mapped";
  }
};

const getDataType = (propertyName, prefix) => {
  if (propertyName.toLowerCase().includes("email")) return DATA_TYPE_EMAIL;
  else if (prefix === "ap" && propertyName === "name") return DATA_TYPE_HYPERLINK;
  else if (propertyName === "phoneNumber" || propertyName.toLowerCase().includes("phone"))
    return DATA_TYPE_PHONE_NUMBER;
};

// also used in OrderInformation for QA page
export const getFieldRepFields = ({
  fieldRep,
  rights,
  caseStatus,
  FRList,
  enabledFeatures,
  completedDate,
  isEditable,
}) => {
  const fields = constructUserContactInformation(fieldRep, "fr");
  let result = {};
  if (
    fields.length > 0 &&
    (rights.includes(FIELDREP_TOOLTIPS_ME) ||
      (enabledFeatures?.map((s) => s.feature).includes(MAJOR_COMPANY_VIEW_FR_TOOLTIP) && !completedDate))
  ) {
    result = {
      key: "fr-header",
      label: CaseFieldLabels.Inspector.getLabel(),
      value: fieldRep?.nameNumberDto?.firstLast,
      tooltip: { title: CaseFieldLabels.Inspector.getLabel() + " Information", fields },
      editable: false,
    };
  } else {
    result = {
      key: "fr-header",
      label: CaseFieldLabels.Inspector.getLabel(),
      value: rights.includes(FIELDREP_TOOLTIPS_ME) ? fieldRep.name : fieldRep.number,
      editable: false,
    };
  }
  if (isEditable) {
    return {
      ...result,
      editFields: [
        {
          key: "inspectorNumber",
          label: CaseFieldLabels.Inspector.getLabel(),
          dataType: DATA_TYPE_DROPDOWN2,
          value: fieldRep && fieldRep.name && { label: fieldRep.name, value: fieldRep.number },
          dataTypeOptions: {
            options: FRList && FRList.map((x) => ({ label: x.fullName, value: x.number })),
            nullable: true,
            sortFunc: getFRSortFunc(),
          },
          editable: caseStatus !== "COMPLETED",
        },
      ],
    };
  } else {
    return result;
  }
};

const mapStateToProps = (state) => {
  return {
    isFetchingAssignableAppraisers: state.caseDetails.isFetchingAssignableAppraisers,
    assignableAppraisers: state.caseDetails.assignableAppraisers,
    isChangingAppraiserAssigned: state.caseDetails.isChangingAppraiserAssigned,
    unassignStatus: state.assignment.unassignStatus,
    assignStatus: state.assignment.assignStatus,
    rights: state.user.profile.rights,
  };
};
const mapDispatchToProps = (dispatch) => ({
  getAppraiserListByAppraisalId(appraisalId) {
    dispatch(getAppraiserListByAppraisalId(appraisalId));
  },
  changeAppraiserAssigned(appraisalId, appraiserId, callback, caseAssignmentDetails = null) {
    dispatch(changeAppraiserAssigned(appraisalId, appraiserId, callback, caseAssignmentDetails));
  },
  // although it says inspector, the api can also be used to assign appraiser
  assignCasesToInspector(mode, caseIds, inspectorNumber, callback = null, caseAssignmentDetails = null) {
    dispatch(assignCasesToInspector(mode, caseIds, inspectorNumber, callback, caseAssignmentDetails));
  },
  unassignCases(mode, caseIds, callback = null) {
    dispatch(unassignCases(mode, caseIds, callback));
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
});
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ContactInformation));
